import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, Edit, Calendar, FileText, UserCheck, RefreshCw, Link2, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TYPES = {
  PORTIERE: { label: 'Portiere', color: 'bg-blue-100 text-blue-800' },
  GIARDINIERE: { label: 'Giardiniere', color: 'bg-green-100 text-green-800' },
  MANUTENTORE: { label: 'Manutentore', color: 'bg-orange-100 text-orange-800' },
  RSPP: { label: 'RSPP', color: 'bg-red-100 text-red-800' },
  ADDETTO_PULIZIE: { label: 'Addetto Pulizie', color: 'bg-purple-100 text-purple-800' },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800' }
};

const EMPLOYMENT_TYPES = {
  FULL_TIME: 'Tempo Pieno',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contratto a Termine',
  EXTERNAL: 'Collaboratore Esterno'
};

const STATUS_BADGES = {
  ACTIVE: { label: 'Attivo', color: 'bg-green-100 text-green-800' },
  INACTIVE: { label: 'Inattivo', color: 'bg-yellow-100 text-yellow-800' },
  TERMINATED: { label: 'Cessato', color: 'bg-red-100 text-red-800' }
};

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingStaff, setLinkingStaff] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingStaff, setDeletingStaff] = useState(null);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { token, selectedCondominium, userRole } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    fiscal_code: '',
    role_type: 'PORTIERE',
    employment_type: 'FULL_TIME',
    inquadramento: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    matricola_inps: '',
    matricola_inail: '',
    codice_ditta: '',
    iban: '',
    weekly_hours: 40,
    notes: ''
  });

  const condoId = selectedCondominium?.condominium?.id;
  // Check if user is admin from role or from selected condominium
  const isAdmin = userRole === 'ADMIN' || selectedCondominium?.role === 'ADMIN';

  const fetchStaff = useCallback(async () => {
    if (!token || !condoId) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setStaff(data.staff || []);
      } else if (res.status === 403) {
        toast.error('Accesso non autorizzato');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Errore caricamento personale');
    } finally {
      setLoading(false);
    }
  }, [token, condoId, navigate]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      fiscal_code: '',
      role_type: 'PORTIERE',
      employment_type: 'FULL_TIME',
      inquadramento: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      matricola_inps: '',
      matricola_inail: '',
      codice_ditta: '',
      iban: '',
      weekly_hours: 40,
      notes: ''
    });
    setEditingStaff(null);
  };

  const openEditDialog = (member) => {
    setEditingStaff(member);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      fiscal_code: member.fiscal_code || '',
      role_type: member.role_type || 'PORTIERE',
      employment_type: member.employment_type || 'FULL_TIME',
      inquadramento: member.inquadramento || '',
      start_date: member.start_date || '',
      end_date: member.end_date || '',
      matricola_inps: member.matricola_inps || '',
      matricola_inail: member.matricola_inail || '',
      codice_ditta: member.codice_ditta || '',
      iban: member.iban || '',
      weekly_hours: member.weekly_hours || 40,
      notes: member.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingStaff 
        ? `${API_URL}/api/condominiums/${condoId}/staff/${editingStaff.id}`
        : `${API_URL}/api/condominiums/${condoId}/staff`;
      
      const res = await fetch(url, {
        method: editingStaff ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingStaff ? 'Dipendente aggiornato' : 'Dipendente creato');
        setDialogOpen(false);
        resetForm();
        fetchStaff();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  // Fetch users for linking
  const fetchAvailableUsers = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/members`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        // Filter out users already linked to staff
        const linkedUserIds = staff.filter(s => s.linked_user_id).map(s => s.linked_user_id);
        const available = (data.members || []).filter(m => !linkedUserIds.includes(m.user_id));
        setAvailableUsers(available);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const openLinkDialog = async (member) => {
    setLinkingStaff(member);
    await fetchAvailableUsers();
    setSelectedUserId('');
    setLinkDialogOpen(true);
  };

  const handleLinkUser = async () => {
    if (!linkingStaff || !selectedUserId) {
      toast.error('Seleziona un utente da collegare');
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('user_id', selectedUserId);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${linkingStaff.id}/link-user`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Utente collegato al dipendente. L\'utente può ora accedere al portale dipendente.');
        setLinkDialogOpen(false);
        setLinkingStaff(null);
        fetchStaff();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore collegamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  // Prima di eliminare si chiede al backend cosa c'è attaccato alla scheda.
  const openDeleteDialog = async (member) => {
    setDeletingStaff(member);
    setDeleteCheck(null);
    setDeleteDialogOpen(true);

    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${member.id}/delete-check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setDeleteCheck(await res.json());
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore verifica dipendente');
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      toast.error('Errore di connessione');
      setDeleteDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingStaff) return;

    setDeleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${deletingStaff.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        toast.success(`${deletingStaff.first_name} ${deletingStaff.last_name} eliminato`);
        setDeleteDialogOpen(false);
        setDeletingStaff(null);
        setDeleteCheck(null);
        fetchStaff();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setDeleting(false);
    }
  };

  // Alternativa quando la scheda non si può eliminare: si archivia.
  const handleArchive = async () => {
    if (!deletingStaff) return;

    setDeleting(true);
    try {
      const body = new FormData();
      body.append('new_status', 'TERMINATED');

      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${deletingStaff.id}/status`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body
        }
      );

      if (res.ok) {
        toast.success(`${deletingStaff.first_name} ${deletingStaff.last_name} messo a Cessato`);
        setDeleteDialogOpen(false);
        setDeletingStaff(null);
        setDeleteCheck(null);
        fetchStaff();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore aggiornamento stato');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Personale"
        subtitle="Gestione dello staff"
        actions={isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Dipendente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingStaff ? 'Modifica Dipendente' : 'Nuovo Dipendente'}
                </DialogTitle>
                <DialogDescription>
                  Inserisci i dati del dipendente
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Mario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Rossi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="mario.rossi@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Codice Fiscale</Label>
                  <Input
                    value={formData.fiscal_code}
                    onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                    placeholder="RSSMRA80A01H501X"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select 
                    value={formData.role_type} 
                    onValueChange={(v) => setFormData({...formData, role_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo Contratto</Label>
                  <Select 
                    value={formData.employment_type} 
                    onValueChange={(v) => setFormData({...formData, employment_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EMPLOYMENT_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Inquadramento (CCNL)</Label>
                  <Input
                    value={formData.inquadramento}
                    onChange={(e) => setFormData({...formData, inquadramento: e.target.value})}
                    placeholder="Es: Livello C1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Assunzione *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Fine Contratto</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ore Settimanali</Label>
                  <Input
                    type="number"
                    value={formData.weekly_hours}
                    onChange={(e) => setFormData({...formData, weekly_hours: parseFloat(e.target.value) || 40})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={formData.iban}
                    onChange={(e) => setFormData({...formData, iban: e.target.value.toUpperCase()})}
                    placeholder="IT60X0542811101000000123456"
                  />
                </div>
                
                <div className="col-span-2 pt-4 border-t">
                  <h4 className="font-medium mb-3">Dati INPS/INAIL</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Matricola INPS</Label>
                      <Input
                        value={formData.matricola_inps}
                        onChange={(e) => setFormData({...formData, matricola_inps: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Matricola INAIL</Label>
                      <Input
                        value={formData.matricola_inail}
                        onChange={(e) => setFormData({...formData, matricola_inail: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Codice Ditta</Label>
                      <Input
                        value={formData.codice_ditta}
                        onChange={(e) => setFormData({...formData, codice_ditta: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Note</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}>
                  Annulla
                </Button>
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingStaff ? 'Salva Modifiche' : 'Crea Dipendente'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Totale Dipendenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{staff.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Attivi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {staff.filter(s => s.status === 'ACTIVE').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Con Account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {staff.filter(s => s.has_user_account).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tempo Pieno</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {staff.filter(s => s.employment_type === 'FULL_TIME').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Personale</CardTitle>
          <CardDescription>
            Clicca su un dipendente per gestire presenze e assenze
          </CardDescription>
        </CardHeader>
        <CardContent>
          {staff.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun dipendente registrato</p>
              {isAdmin && (
                <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi il primo dipendente
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dipendente</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Contratto</TableHead>
                  <TableHead>Data Assunzione</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => {
                  const roleInfo = ROLE_TYPES[member.role_type] || ROLE_TYPES.ALTRO;
                  const statusInfo = STATUS_BADGES[member.status] || STATUS_BADGES.ACTIVE;
                  
                  return (
                    <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="font-semibold text-primary">
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium">{member.first_name} {member.last_name}</div>
                            <div className="text-xs text-muted-foreground">{member.email || '-'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleInfo.color}>
                          {roleInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {EMPLOYMENT_TYPES[member.employment_type] || member.employment_type}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(member.start_date)}</TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {member.has_user_account ? (
                          <UserCheck className="h-4 w-4 text-green-600" title="Account collegato" />
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => openLinkDialog(member)}
                            title="Collega account utente"
                          >
                            <Link2 className="h-3 w-3 mr-1" />
                            Collega
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/staff/${member.id}`)}
                            title="Presenze & Assenze"
                          >
                            <Calendar className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/staff/${member.id}/documents`)}
                            title="Documenti"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(member)}
                              title="Modifica"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(member)}
                              title="Elimina dipendente"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Link User Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collega Account Utente</DialogTitle>
            <DialogDescription>
              {linkingStaff && (
                <>Collega un account utente a <strong>{linkingStaff.first_name} {linkingStaff.last_name}</strong>. 
                L'utente potrà accedere al portale dipendente per gestire presenze e assenze.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seleziona Utente</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un utente..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="_none" disabled>Nessun utente disponibile</SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.user_full_name || user.user_email} ({user.role})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Gli utenti già collegati ad altri dipendenti non vengono mostrati.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleLinkUser} disabled={saving || !selectedUserId}>
              {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Collega Utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Elimina Dipendente */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeletingStaff(null);
          setDeleteCheck(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Dipendente</DialogTitle>
            <DialogDescription>
              {deletingStaff && (
                <>Stai per eliminare <strong>{deletingStaff.first_name} {deletingStaff.last_name}</strong>.
                L'operazione non si può annullare.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!deleteCheck ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Controllo cosa è collegato alla scheda...
              </div>
            ) : deleteCheck.eliminabile ? (
              <>
                <p className="text-sm">Insieme alla scheda verranno rimossi:</p>
                <ul className="text-sm list-disc pl-5 space-y-1">
                  <li>{deleteCheck.presenze} registrazioni di presenza</li>
                  <li>{deleteCheck.assenze} richieste di assenza</li>
                  {deleteCheck.account_collegato && (
                    <li>
                      il collegamento con l'account utente — l'utente resta, ma non
                      vedrà più il portale dipendente
                    </li>
                  )}
                </ul>
                <p className="text-xs text-muted-foreground">
                  La scheda eliminata resta nel registro delle attività.
                </p>
              </>
            ) : (
              <div className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{deleteCheck.motivo}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            {deleteCheck && !deleteCheck.eliminabile && deleteCheck.stato !== 'TERMINATED' && (
              <Button variant="secondary" onClick={handleArchive} disabled={deleting}>
                {deleting && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Metti a Cessato
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || !deleteCheck?.eliminabile}
            >
              {deleting && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
